import { useRef, useState } from 'react'
import type { DragEvent, ReactNode } from 'react'
import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import './FileDropZone.css'

export interface FileDropZoneProps {
  /** "Arrastra el archivo aquí o" */
  prompt: ReactNode
  /** "Elegir archivo" */
  buttonLabel: ReactNode
  /** "JPG, PNG o PDF, hasta 10 MB" */
  hint?: ReactNode
  /** Tipos aceptados por el selector propio */
  accept?: string
  /** Recibe los archivos soltados o elegidos */
  onFiles: (files: File[]) => void
  /**
   * Si se pasa, "Elegir archivo" llama aquí en lugar de abrir el selector propio
   * (captura de documento: el selector es el del SDK, inventario D9).
   */
  onChoose?: () => void
  className?: string
}

/**
 * Zona de arrastre (bundle B9 §9b, escritorio): borde discontinuo, fondo sunken, icono de subida,
 * "Elegir archivo" y formatos. Sustituye a la cámara en escritorio.
 */
export function FileDropZone({ prompt, buttonLabel, hint, accept, onFiles, onChoose, className }: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) onFiles(files)
  }
  return (
    // Soltar es un atajo de ratón; el teclado y el lector de pantalla usan el botón "Elegir archivo".
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cx('hp-dropzone', over && 'hp-dropzone--over', className)}
      onDragOver={(e) => {
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <Icon name="upload" size={40} className="hp-dropzone__icon" />
      <p className="hp-dropzone__prompt">{prompt}</p>
      <Button variant="secondary" fullWidth={false} onClick={() => (onChoose ? onChoose() : inputRef.current?.click())}>
        {buttonLabel}
      </Button>
      {hint && <p className="hp-dropzone__hint">{hint}</p>}
      {!onChoose && (
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          tabIndex={-1}
          aria-hidden="true"
          className="hp-dropzone__input"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? [])
            if (files.length > 0) onFiles(files)
            e.target.value = ''
          }}
        />
      )}
    </div>
  )
}
